import React, {
    useEffect,
    useMemo,
    useState
} from "react";

import { Link } from "react-router-dom";

import API_BASE_URL from "../config/api";


function PaperQuestions() {

    /*
    |--------------------------------------------------------------------------
    | API
    |--------------------------------------------------------------------------
    */

    const API_URL =
        `${API_BASE_URL}/admin/paper-questions`;


    /*
    |--------------------------------------------------------------------------
    | State
    |--------------------------------------------------------------------------
    */

    const [questions, setQuestions] = useState([]);

    const [loading, setLoading] = useState(true);

    const [error, setError] = useState("");

    const [search, setSearch] = useState("");

    const [currentPage, setCurrentPage] = useState(1);


    /*
    |--------------------------------------------------------------------------
    | Pagination
    |--------------------------------------------------------------------------
    */

    const itemsPerPage = 10;


    /*
    |--------------------------------------------------------------------------
    | Remove HTML From Question / Explanation
    |--------------------------------------------------------------------------
    */

    const getText = (html) => {

        if (!html) {
            return "";
        }


        const tempDiv =
            document.createElement("div");


        tempDiv.innerHTML = html;


        const text =
            tempDiv.textContent ||
            tempDiv.innerText ||
            "";


        return text.trim();

    };


    /*
    |--------------------------------------------------------------------------
    | Fetch Questions
    |--------------------------------------------------------------------------
    */

    const fetchQuestions = async () => {

        try {

            setLoading(true);

            setError("");


            /*
            |--------------------------------------------------------------------------
            | API Request
            |--------------------------------------------------------------------------
            */

            const response =
                await fetch(
                    `${API_URL}?per_page=100`,
                    {
                        method: "GET",

                        headers: {
                            Accept:
                                "application/json"
                        },

                        credentials: "include"
                    }
                );


            /*
            |--------------------------------------------------------------------------
            | Unauthorized
            |--------------------------------------------------------------------------
            */

            if (response.status === 401) {

                throw new Error(
                    "Admin authentication failed. Please login again."
                );

            }


            /*
            |--------------------------------------------------------------------------
            | Server Error
            |--------------------------------------------------------------------------
            */

            if (!response.ok) {

                const errorText =
                    await response.text();

                console.error(
                    "Paper Questions Server Error:",
                    response.status,
                    errorText
                );


                throw new Error(
                    `Server Error: ${response.status}`
                );

            }


            /*
            |--------------------------------------------------------------------------
            | JSON
            |--------------------------------------------------------------------------
            */

            const data =
                await response.json();


            console.log(
                "Paper Questions API Response:",
                data
            );


            /*
            |--------------------------------------------------------------------------
            | Success
            |--------------------------------------------------------------------------
            */

            if (data.success) {

                setQuestions(
                    Array.isArray(data.data)
                        ? data.data
                        : []
                );


                setCurrentPage(1);

            } else {

                setError(
                    data.message ||
                    "Unable to fetch paper questions."
                );

            }

        } catch (error) {

            console.error(
                "Paper Questions API Error:",
                error
            );


            setError(
                error.message ||
                "Unable to connect with server."
            );

        } finally {

            setLoading(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Fetch On Load
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        fetchQuestions();

    }, []);


    /*
    |--------------------------------------------------------------------------
    | Search
    |--------------------------------------------------------------------------
    */

    const filteredQuestions =
        useMemo(() => {

            const value =
                search
                    .trim()
                    .toLowerCase();


            /*
            |--------------------------------------------------------------------------
            | No Search
            |--------------------------------------------------------------------------
            */

            if (!value) {

                return questions;

            }


            /*
            |--------------------------------------------------------------------------
            | Filter Questions
            |--------------------------------------------------------------------------
            */

            return questions.filter(
                (question) => {


                    /*
                    |--------------------------------------------------------------------------
                    | Study ID
                    |--------------------------------------------------------------------------
                    */

                    const studyId =
                        String(
                            question.study_id || ""
                        )
                            .toLowerCase();


                    /*
                    |--------------------------------------------------------------------------
                    | Subject
                    |--------------------------------------------------------------------------
                    */

                    const subject =
                        String(
                            question.subject || ""
                        )
                            .toLowerCase();


                    /*
                    |--------------------------------------------------------------------------
                    | Paper
                    |--------------------------------------------------------------------------
                    */

                    const paper =
                        String(
                            question.paper || ""
                        )
                            .toLowerCase();


                    /*
                    |--------------------------------------------------------------------------
                    | Question
                    |--------------------------------------------------------------------------
                    */

                    const questionText =
                        getText(
                            question.question
                        )
                            .toLowerCase();


                    /*
                    |--------------------------------------------------------------------------
                    | Option 1
                    |--------------------------------------------------------------------------
                    */

                    const option1 =
                        getText(
                            question.option1
                        )
                            .toLowerCase();


                    /*
                    |--------------------------------------------------------------------------
                    | Option 2
                    |--------------------------------------------------------------------------
                    */

                    const option2 =
                        getText(
                            question.option2
                        )
                            .toLowerCase();


                    /*
                    |--------------------------------------------------------------------------
                    | Option 3
                    |--------------------------------------------------------------------------
                    */

                    const option3 =
                        getText(
                            question.option3
                        )
                            .toLowerCase();


                    /*
                    |--------------------------------------------------------------------------
                    | Option 4
                    |--------------------------------------------------------------------------
                    */

                    const option4 =
                        getText(
                            question.option4
                        )
                            .toLowerCase();


                    /*
                    |--------------------------------------------------------------------------
                    | Right Option
                    |--------------------------------------------------------------------------
                    */

                    const rightOption =
                        String(
                            question.right_option || ""
                        )
                            .toLowerCase();


                    /*
                    |--------------------------------------------------------------------------
                    | Sort Order
                    |--------------------------------------------------------------------------
                    */

                    const sortOrder =
                        String(
                            question.sort_order || ""
                        )
                            .toLowerCase();


                    /*
                    |--------------------------------------------------------------------------
                    | Explanation
                    |--------------------------------------------------------------------------
                    */

                    const explanation =
                        getText(
                            question.explanation
                        )
                            .toLowerCase();


                    /*
                    |--------------------------------------------------------------------------
                    | Search All Fields
                    |--------------------------------------------------------------------------
                    */

                    return (

                        studyId.includes(value) ||

                        subject.includes(value) ||

                        paper.includes(value) ||

                        questionText.includes(value) ||

                        option1.includes(value) ||

                        option2.includes(value) ||

                        option3.includes(value) ||

                        option4.includes(value) ||

                        rightOption.includes(value) ||

                        sortOrder.includes(value) ||

                        explanation.includes(value)

                    );

                }
            );

        }, [questions, search]);


    /*
    |--------------------------------------------------------------------------
    | Total Pages
    |--------------------------------------------------------------------------
    */

    const totalPages =
        Math.ceil(
            filteredQuestions.length /
            itemsPerPage
        );


    /*
    |--------------------------------------------------------------------------
    | Start Index
    |--------------------------------------------------------------------------
    */

    const startIndex =
        (currentPage - 1) *
        itemsPerPage;


    /*
    |--------------------------------------------------------------------------
    | Paginated Questions
    |--------------------------------------------------------------------------
    */

    const paginatedQuestions =
        filteredQuestions.slice(
            startIndex,
            startIndex + itemsPerPage
        );


    /*
    |--------------------------------------------------------------------------
    | Search Handler
    |--------------------------------------------------------------------------
    */

    const handleSearch = (event) => {

        setSearch(
            event.target.value
        );

        setCurrentPage(1);

    };


    /*
    |--------------------------------------------------------------------------
    | Previous
    |--------------------------------------------------------------------------
    */

    const handlePrevious = () => {

        if (currentPage > 1) {

            setCurrentPage(
                currentPage - 1
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Next
    |--------------------------------------------------------------------------
    */

    const handleNext = () => {

        if (
            currentPage < totalPages
        ) {

            setCurrentPage(
                currentPage + 1
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    if (loading) {

        return (

            <div
                className="
                    container-fluid
                    d-flex
                    justify-content-center
                    align-items-center
                "
                style={{
                    minHeight: "400px"
                }}
            >

                <div className="text-center">

                    <div
                        className="
                            spinner-border
                            text-primary
                        "
                        role="status"
                    >
                    </div>


                    <div
                        className="
                            mt-3
                            text-muted
                        "
                    >
                        Loading paper questions...
                    </div>

                </div>

            </div>

        );

    }


    /*
    |--------------------------------------------------------------------------
    | Main
    |--------------------------------------------------------------------------
    */

    return (

        <div className="container-fluid">


            {/* =====================================================
                Page Header
            ===================================================== */}

            <div
                className="
                    d-flex
                    justify-content-between
                    align-items-center
                    mb-4
                "
            >

                <div>

                    <h3 className="mb-1">
                        Paper Questions
                    </h3>


                    <p className="text-muted mb-0">
                        Manage study material questions
                    </p>

                </div>


                {/* =================================================
                    Top Buttons
                ================================================= */}

                <div className="d-flex gap-2">


                    {/* Refresh */}

                    <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={fetchQuestions}
                    >

                        <i
                            className="
                                fa
                                fa-refresh
                                me-2
                            "
                        ></i>

                        Refresh

                    </button>


                    {/* Add Question */}

                    <Link
                        to="/add-paper-question"
                        className="btn btn-primary"
                    >

                        <i
                            className="
                                fa
                                fa-plus
                                me-2
                            "
                        ></i>

                        Add Paper Question

                    </Link>

                </div>

            </div>


            {/* =====================================================
                Error
            ===================================================== */}

            {error && (

                <div
                    className="
                        alert
                        alert-danger
                        d-flex
                        justify-content-between
                        align-items-center
                    "
                >

                    <span>
                        {error}
                    </span>


                    <button
                        type="button"
                        className="
                            btn
                            btn-sm
                            btn-danger
                        "
                        onClick={fetchQuestions}
                    >

                        Try Again

                    </button>

                </div>

            )}


            {/* =====================================================
                Card
            ===================================================== */}

            <div className="card shadow-sm">


                {/* =================================================
                    Card Header
                ================================================= */}

                <div className="card-header bg-white">

                    <div className="row align-items-center">


                        {/* Search */}

                        <div className="col-md-7">

                            <div
                                className="input-group"
                            >

                                <span
                                    className="
                                        input-group-text
                                        bg-white
                                    "
                                >

                                    <i
                                        className="
                                            fa
                                            fa-search
                                        "
                                    ></i>

                                </span>


                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="
                                        Search question, study ID, subject, paper, answer, option or explanation...
                                    "
                                    value={search}
                                    onChange={
                                        handleSearch
                                    }
                                />

                            </div>

                        </div>


                        {/* Total */}

                        <div
                            className="
                                col-md-5
                                text-md-end
                                mt-3
                                mt-md-0
                            "
                        >

                            <span className="text-muted">
                                Total Questions:
                            </span>


                            <strong className="ms-2">
                                {filteredQuestions.length}
                            </strong>

                        </div>

                    </div>

                </div>


                {/* =================================================
                    Table
                ================================================= */}

                <div className="card-body p-0">

                    <div
                        className="table-responsive"
                        style={{
                            maxHeight: "320px"
                        }}
                    >

                        <table
                            className="
                                table
                                table-bordered
                                table-hover
                                align-middle
                                mb-0
                            "
                        >


                            {/* =================================================
                                Header
                            ================================================= */}

                            <thead
                                className="table-light"
                                style={{
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 5
                                }}
                            >

                                <tr>


                                    {/* # */}

                                    <th
                                        className="text-center"
                                        style={{
                                            minWidth: "60px"
                                        }}
                                    >
                                        #
                                    </th>


                                    {/* Study ID */}

                                    <th
                                        className="text-center"
                                        style={{
                                            minWidth: "90px"
                                        }}
                                    >
                                        Study ID
                                    </th>


                                    {/* Question */}

                                    <th
                                        style={{
                                            minWidth: "400px"
                                        }}
                                    >
                                        Question
                                    </th>


                                    {/* Subject */}

                                    <th
                                        style={{
                                            minWidth: "150px"
                                        }}
                                    >
                                        Subject
                                    </th>


                                    {/* Paper */}

                                    <th
                                        style={{
                                            minWidth: "150px"
                                        }}
                                    >
                                        Paper
                                    </th>


                                    {/* Answer */}

                                    <th
                                        className="text-center"
                                        style={{
                                            minWidth: "100px"
                                        }}
                                    >
                                        Answer
                                    </th>


                                    {/* Sort Order */}

                                    <th
                                        className="text-center"
                                        style={{
                                            minWidth: "100px"
                                        }}
                                    >
                                        Sort Order
                                    </th>


                                    {/* Action */}

                                    <th
                                        className="text-center"
                                        style={{
                                            minWidth: "110px"
                                        }}
                                    >
                                        Action
                                    </th>

                                </tr>

                            </thead>


                            {/* =================================================
                                Body
                            ================================================= */}

                            <tbody>

                                {paginatedQuestions.length >
                                0 ? (

                                    paginatedQuestions.map(
                                        (
                                            item,
                                            index
                                        ) => {


                                            const questionText =
                                                getText(
                                                    item.question
                                                );


                                            return (

                                                <tr
                                                    key={
                                                        item.study_id ||
                                                        index
                                                    }
                                                >


                                                    {/* =================================================
                                                        #
                                                    ================================================= */}

                                                    <td
                                                        className="
                                                            text-center
                                                        "
                                                    >

                                                        {
                                                            startIndex +
                                                            index +
                                                            1
                                                        }

                                                    </td>


                                                    {/* =================================================
                                                        Study ID
                                                    ================================================= */}

                                                    <td
                                                        className="
                                                            text-center
                                                        "
                                                    >

                                                        <span
                                                            className="
                                                                badge
                                                                bg-light
                                                                text-dark
                                                                border
                                                            "
                                                        >

                                                            {
                                                                item.study_id ||
                                                                "-"
                                                            }

                                                        </span>

                                                    </td>


                                                    {/* =================================================
                                                        Question
                                                    ================================================= */}

                                                    <td>

                                                        <div
                                                            title={
                                                                questionText
                                                            }
                                                            style={{
                                                                maxWidth:
                                                                    "500px",
                                                                whiteSpace:
                                                                    "nowrap",
                                                                overflow:
                                                                    "hidden",
                                                                textOverflow:
                                                                    "ellipsis"
                                                            }}
                                                        >

                                                            {
                                                                questionText ||
                                                                "-"
                                                            }

                                                        </div>

                                                    </td>


                                                    {/* =================================================
                                                        Subject
                                                    ================================================= */}

                                                    <td>

                                                        {
                                                            item.subject ||
                                                            "-"
                                                        }

                                                    </td>


                                                    {/* =================================================
                                                        Paper
                                                    ================================================= */}

                                                    <td>

                                                        {
                                                            item.paper ||
                                                            "-"
                                                        }

                                                    </td>


                                                    {/* =================================================
                                                        Answer
                                                    ================================================= */}

                                                    <td
                                                        className="
                                                            text-center
                                                        "
                                                    >

                                                        <span
                                                            className="
                                                                badge
                                                                bg-success
                                                            "
                                                        >

                                                            {
                                                                item.right_option ||
                                                                "-"
                                                            }

                                                        </span>

                                                    </td>


                                                    {/* =================================================
                                                        Sort Order
                                                    ================================================= */}

                                                    <td
                                                        className="
                                                            text-center
                                                        "
                                                    >

                                                        {
                                                            item.sort_order ??
                                                            "-"
                                                        }

                                                    </td>


                                                    {/* =================================================
                                                        Edit
                                                    ================================================= */}

                                                    <td
                                                        className="
                                                            text-center
                                                        "
                                                    >

                                                        <Link
                                                            to={
                                                                `/edit-paper-question/${encodeURIComponent(
                                                                    item.study_id
                                                                )}`
                                                            }
                                                            className="
                                                                btn
                                                                btn-sm
                                                                btn-outline-primary
                                                            "
                                                            title="Edit Question"
                                                        >

                                                            <i
                                                                className="
                                                                    fa
                                                                    fa-edit
                                                                    me-1
                                                                "
                                                            ></i>

                                                            Edit

                                                        </Link>

                                                    </td>

                                                </tr>

                                            );

                                        }
                                    )

                                ) : (

                                    <tr>

                                        <td
                                            colSpan="8"
                                            className="
                                                text-center
                                                py-5
                                                text-muted
                                            "
                                        >

                                            <i
                                                className="
                                                    fa
                                                    fa-question-circle
                                                    fs-3
                                                    mb-3
                                                "
                                            ></i>


                                            <div>
                                                No paper questions found.
                                            </div>

                                        </td>

                                    </tr>

                                )}

                            </tbody>

                        </table>

                    </div>

                </div>


                {/* =================================================
                    Pagination
                ================================================= */}

                {filteredQuestions.length > 0 && (

                    <div
                        className="
                            card-footer
                            bg-white
                            d-flex
                            justify-content-between
                            align-items-center
                        "
                    >


                        {/* Showing */}

                        <div
                            className="text-muted"
                        >

                            Showing{" "}

                            <strong>
                                {startIndex + 1}
                            </strong>

                            {" "}to{" "}

                            <strong>
                                {Math.min(
                                    startIndex +
                                    itemsPerPage,
                                    filteredQuestions.length
                                )}
                            </strong>

                            {" "}of{" "}

                            <strong>
                                {filteredQuestions.length}
                            </strong>

                        </div>


                        {/* Buttons */}

                        <div
                            className="btn-group"
                        >

                            {/* Previous */}

                            <button
                                type="button"
                                className="
                                    btn
                                    btn-outline-primary
                                "
                                disabled={
                                    currentPage === 1
                                }
                                onClick={
                                    handlePrevious
                                }
                            >

                                Previous

                            </button>


                            {/* Current Page */}

                            <button
                                type="button"
                                className="
                                    btn
                                    btn-primary
                                "
                                disabled
                            >

                                {currentPage}

                                {" / "}

                                {totalPages || 1}

                            </button>


                            {/* Next */}

                            <button
                                type="button"
                                className="
                                    btn
                                    btn-outline-primary
                                "
                                disabled={
                                    currentPage >=
                                    totalPages
                                }
                                onClick={
                                    handleNext
                                }
                            >

                                Next

                            </button>

                        </div>

                    </div>

                )}

            </div>

        </div>

    );

}


export default PaperQuestions;